import React, { useEffect, useState, useCallback } from "react";
import AppWrapper from "../AppWrapper";
import {
  Page,
  LegacyCard,
  IndexTable,
  Text,
  Spinner,
  BlockStack,
  Tabs,
  Card,
  InlineStack,
  TextField,
  Button,
  Banner,
  Select,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { useNavigate } from "react-router-dom";
import { levelsText, StatusBadge, fmtMoney, fmtDateTime } from "./washUtil";
import { formatPlate } from "../../utility/funcs";

const TABS = [
  { id: "flagged", content: "Por revisar" },
  { id: "open", content: "En curso" },
  { id: "all", content: "Todos" },
];

// Prices per wash level. A wash keeps the price it had when it was saved.
function WashPrices() {
  const [types, setTypes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    supabase
      .from("wash_types")
      .select("key, name, price, min_minutes")
      .order("position")
      .then(({ data }) => setTypes(data || []));
  }, []);

  const edit = (key, field, value) =>
    setTypes((ts) => ts.map((t) => (t.key === key ? { ...t, [field]: value } : t)));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    for (const t of types) {
      const { data, error } = await supabase
        .from("wash_types")
        .update({ price: Number(t.price) || 0, min_minutes: parseInt(t.min_minutes) || 0 })
        .eq("key", t.key)
        .select("key");
      if (error || !data?.length) {
        setMsg({ tone: "critical", text: error?.message || "No tienes permiso para cambiar precios." });
        setSaving(false);
        return;
      }
    }
    setMsg({ tone: "success", text: "Precios guardados. Aplican a los lavados nuevos." });
    setSaving(false);
  };

  if (!types) return null;
  return (
    <Card>
      <BlockStack gap="300">
        <Text variant="headingSm">Pago por lavado</Text>
        {msg && <Banner tone={msg.tone}>{msg.text}</Banner>}
        <InlineStack gap="400" wrap>
          {types.map((t) => (
            <BlockStack key={t.key} gap="100">
              <Text variant="bodySm" fontWeight="semibold">{t.name}</Text>
              <InlineStack gap="200">
                <div style={{ width: 110 }}>
                  <TextField
                    label="Pago"
                    labelHidden
                    prefix="$"
                    type="number"
                    value={String(t.price ?? "")}
                    onChange={(v) => edit(t.key, "price", v)}
                    autoComplete="off"
                  />
                </div>
                <div style={{ width: 110 }}>
                  <TextField
                    label="Mínimo"
                    labelHidden
                    suffix="min"
                    type="number"
                    value={String(t.min_minutes ?? "")}
                    onChange={(v) => edit(t.key, "min_minutes", v)}
                    autoComplete="off"
                    helpText="Menos tiempo → por revisar"
                  />
                </div>
              </InlineStack>
            </BlockStack>
          ))}
        </InlineStack>
        <InlineStack>
          <Button onClick={save} loading={saving}>Guardar precios</Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

function Washes() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(""); // "" = todas

  useEffect(() => {
    supabase
      .from("branches")
      .select("id, name")
      .eq("is_active", true)
      .eq("segment", "corto_plazo")
      .order("name")
      .then(({ data }) => setBranches(data || []));
  }, []);

  const load = useCallback(async () => {
    setRows(null);
    let q = supabase
      .from("car_washes")
      .select(
        "id, levels, price_total, started_at, duration_min, ai_score, review_status, branch:branches(name), car:cars(plate, model:car_models(name, show_as)), staff:users!staff_id(name)"
      )
      .order("started_at", { ascending: false })
      .limit(300);
    if (TABS[tab].id === "flagged") q = q.eq("review_status", "flagged");
    if (TABS[tab].id === "open") q = q.eq("review_status", "open");
    if (branchId) q = q.eq("branch_id", Number(branchId));
    const { data } = await q;
    setRows(data || []);
  }, [tab, branchId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppWrapper>
      <Page title="Lavados">
        <BlockStack gap="400">
          <WashPrices />
          <LegacyCard>
            <Tabs tabs={TABS} selected={tab} onSelect={setTab} />
            <div style={{ padding: "8px 16px", maxWidth: 260 }}>
              <Select
                label="Sucursal"
                labelInline
                options={[
                  { label: "Todas", value: "" },
                  ...branches.map((b) => ({ label: b.name, value: String(b.id) })),
                ]}
                value={branchId}
                onChange={setBranchId}
              />
            </div>
            {rows === null ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <Spinner size="small" />
              </div>
            ) : (
              <IndexTable
                resourceName={{ singular: "Lavado", plural: "Lavados" }}
                selectable={false}
                itemCount={rows.length}
                emptyState={
                  <div style={{ padding: 24, textAlign: "center" }}>
                    <Text tone="subdued">
                      {TABS[tab].id === "flagged"
                        ? "Nada por revisar."
                        : TABS[tab].id === "open"
                        ? "No hay lavados en curso."
                        : "Sin lavados todavía."}
                    </Text>
                  </div>
                }
                headings={[
                  { title: "Fecha" },
                  { title: "Auto" },
                  { title: "Lavador" },
                  { title: "Sucursal" },
                  { title: "Tipo" },
                  { title: "Duración" },
                  { title: "IA" },
                  { title: "Estatus" },
                  { title: "Pago", alignment: "end" },
                ]}
              >
                {rows.map((r, index) => (
                  <IndexTable.Row
                    id={String(r.id)}
                    key={r.id}
                    position={index}
                    onClick={() => navigate(`/lavados/${r.id}`)}
                  >
                    <IndexTable.Cell>
                      <Text variant="bodySm">{fmtDateTime(r.started_at)}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm" fontWeight="semibold">{formatPlate(r.car?.plate)}</Text>
                      <Text variant="bodySm" tone="subdued">
                        {r.car?.model?.show_as || r.car?.model?.name}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm">{r.staff?.name || "-"}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm">{r.branch?.name || "-"}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm">{levelsText(r.levels)}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm">
                        {r.duration_min != null ? `${r.duration_min} min` : "—"}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm">{r.ai_score != null ? `${r.ai_score}/10` : "—"}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <StatusBadge status={r.review_status} />
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm" alignment="end">{fmtMoney(r.price_total)}</Text>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            )}
          </LegacyCard>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Washes;
